import { KeyValue } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AllowIn, ShortcutEventOutput, ShortcutInput } from 'ng-keyboard-shortcuts';
import {
  Observable,
  OperatorFunction,
  Subject,
  Subscription,
  catchError,
  debounceTime,
  distinctUntilChanged,
  of,
  switchMap,
  take,
  tap,
  map,
  filter,
} from 'rxjs';
import { ObjectModel } from 'src/app/models/object.model';
import { ShortcutModel } from 'src/app/models/shortcut.model';
import { UserDefModel } from 'src/app/models/user-def.model';
import { AutoCompleteService } from 'src/app/services/auto-complete.service';
import { DocumentDataService } from 'src/app/services/document-data.service';
import { ShortcutService } from 'src/app/services/shortcut.service';
import { TextControlService } from 'src/app/services/text-control.service';
import { TextControlFormFieldsService } from 'src/app/services/text-control-formfields.service';
import { UserDefService } from 'src/app/services/user-def.service';
import { EditDocumentComponent } from '../../edit-document/edit-document.component';
import { MaskitoService } from 'src/app/services/maskito.service';
import { DialogService } from 'src/app/services/dialog.service';
import { QuicktextDialogComponent } from '../quicktext-dialog/quicktext-dialog.component';
import { ToastService } from 'src/app/services/toast.service';
import { smartenQuotes } from 'src/app/helpers/smart-quote-helper';

declare const TXTextControl: any;
const stripHtmlRegex = /(<([^>]+)>)/gi;

@Component({
  selector: 'tx-form-field-dialog',
  templateUrl: './form-field-dialog.component.html',
  styleUrls: ['./form-field-dialog.component.scss'],
})
export class FormFieldDialogComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('scrollDetector') scrollDetector = {} as ElementRef;
  @ViewChild('helpTextContainer', { static: false }) helpTextContainer = {} as ElementRef;
  @Input() valueChange?: string;
  @Input() fieldNavigatorSidebarVisible?: boolean;
  @Input('positionEvents') positionEvents!: Observable<any>;
  @Input() pdfAnswersChanged: boolean = false;
  @Input() isActiveFieldSelected: boolean = false;
  @Input() documentDisconnectedFromTXdocsAnswerPool: boolean = false;

  @Output() navigateToNext: EventEmitter<number> = new EventEmitter<number>();
  @Output() valueChanged: EventEmitter<KeyValue<string, string>> = new EventEmitter<KeyValue<string, string>>();
  @Output() lockAnswerPool: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() dockedChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  //public objMask: MaskitoOptions = this.createMaskitoOptions(''); //emptry string will result in universal "/.*/" mask

  public docked: boolean = false;
  //control label style to transtion and disappear on multi line text inputs
  isScrolled = false;

  isInBounds = false;
  public formField: string;
  public helpDescription?: string;
  public helpText?: string;
  public showHelpText = false;
  public value: string;
  public width: number;

  public obj: ObjectModel | null;
  public searching = false;
  public searchFailed = false;
  public shortcuts: ShortcutInput[];
  public isCalculatedField: boolean = false;
  private unsubscribe$ = new Subject<void>();
  private inputPosition: number;
  private selectionEnd: number;
  private positionEventsSubscription!: Subscription;
  private attachBottomOfField = true;
  fldPos: any;
  modal: HTMLElement | null | undefined;
  txtViewLoc = { x: 0, y: 0 };
  divOvr: any;
  mainCanvas: HTMLElement | null | undefined;
  fieldNavigatorSideBar: HTMLElement | null | undefined;
  zoom: number = 1.0;
  isTruncated = true;
  showMoreButton = true; // Add this line
  private modalSizeChange$ = new Subject<void>();
  private repositionModalSubscription: Subscription;
  private inputPositionSubscription$: Subscription;
  public isModalVisible = false;
  private timeoutIdForScrollChanges: any;
  private formFieldToUpdate: string;
  private isDirty: boolean = false;
  public tokenDescription: string | undefined = '';
  public inputType = 'text';

  constructor(
    private documentDataService: DocumentDataService,
    private textControlService: TextControlService,
    private autoCompleteService: AutoCompleteService,
    private userDefService: UserDefService,
    private shortcutService: ShortcutService,
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
    private parent: EditDocumentComponent,
    private textControlFormFieldService: TextControlFormFieldsService,
    private maskitoService: MaskitoService,
    private dialogService: DialogService,
    private toastService: ToastService
  ) {
    this.width = 480;
    this.helpDescription = '';
    this.helpText = '';
    this.value = '';
    this.formField = '';
    this.formFieldToUpdate = '';
    this.obj = null;
    this.shortcuts = [];
    this.inputPosition = 0;
    this.selectionEnd = 0;
    this.repositionModalSubscription = this.modalSizeChange$
      .pipe(
        tap(() => {
          this.setFormFieldPosition();
        })
      )
      .subscribe();
    this.inputPositionSubscription$ = this.textControlService.activeField$.pipe(take(1)).subscribe((ff) => {
      if (!ff) {
        this.formField = '';
      }
    });
  }
  private processingKeyEvent = false;
  @HostListener('keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    // This is to prevent the tab and shift tab from
    // executing at the same time.
    // To see this behavior PRess Tab and Shift tab quickly after
    // changing a formatted field (which will call execute logic)
    // This is caused by the onKeyDown event running code async
    // and processing and saving the answer
    if (this.processingKeyEvent) {
      return;
    }

    this.processingKeyEvent = true;
    this.onKeydown(event);
    this.processingKeyEvent = false;
  }
  @HostListener('window:resize', ['$event'])
  onResize() {
    //always redraw the navigation panel to keep it in sync
    this.setFormFieldPosition();
  }

  ngOnInit(): void {
    this.docked = true;
    this.dockedChanged.emit(this.docked);
    this.getShortcuts();
  }

  ngAfterViewInit(): void {
    this.modal = document.getElementById('form-field-modal');
    this.mainCanvas = document.getElementById('mainCanvas');
    this.fieldNavigatorSideBar = document.getElementById('fieldNavigatorSidebar');
    this.positionEventsSubscription = this.positionEvents.subscribe((event: any) => {
      if (!this.isModalVisible) {
        return;
      }
      this.zoom = event.zoom / 100;
      this.txtViewLoc = event.location;
      this.setFormFieldPosition();
    });
    this.modalSizeChange$.next();

    // recalculate dimensions for the dialog when ribbon bar is collapsed/expanded.
    document
      .getElementById('txBtnRibbonHide')
      ?.addEventListener('click', this.recalculateModalPositionOnToggleRibbonBar.bind(this));
  }

  recalculateModalPositionOnToggleRibbonBar() {
    // Wait for the ribbon  bar to be toggled before we can re-calculate dimensions.
    setTimeout(() => {
      this.setFormFieldPosition();
    }, 100);
  }

  ngOnDestroy(): void {
    document
      .getElementById('txBtnRibbonHide')
      ?.removeEventListener('click', this.recalculateModalPositionOnToggleRibbonBar.bind(this));
    const input = document.getElementById('formFieldValue');
    input?.addEventListener('click', this.updateInputPosition.bind(this));
    input?.addEventListener('keyup', this.updateInputPosition.bind(this));

    if (this.positionEventsSubscription) {
      this.positionEventsSubscription.unsubscribe();
    }
    if (this.repositionModalSubscription) {
      this.repositionModalSubscription.unsubscribe();
    }

    if (this.inputPositionSubscription$) {
      this.inputPositionSubscription$.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['valueChange']?.currentValue != null) {
      this.value = changes['valueChange'].currentValue;
    }

    if (
      this.helpTextContainer &&
      this.helpTextContainer.nativeElement?.scrollHeight <= this.helpTextContainer.nativeElement?.clientHeight
    ) {
    } else {
      this.showMoreButton = true;
    }
    if (changes && changes['isActiveFieldSelected']) {
      this.activeFieldChanging(changes);
    }
  }
  private activeFieldChanging(changes: any) {
    console.debug('activeFieldChanging', changes);
    if (this.textControlService.isFormFieldFocused) {
      if (this.documentDataService.isAnswerChanged(this.formField, this.value)) {
        this.updateFormFieldByEmit();
      }
      this.textControlService.isFormFieldFocused = false;
    }
  }

  toggleTruncated() {
    this.isTruncated = !this.isTruncated;
    setTimeout(() => {
      if (this.helpTextContainer) {
        this.helpTextContainer.nativeElement.scrollTop = 0;
        this.modalSizeChange$.next();
      }
    }, 0);
  }

  public closeModal() {
    this.formField = '';
    if (this.modal) {
      this.isModalVisible = false;
    }
  }
  public lockFieldAnswerPool() {
    this.lockAnswerPool.emit(true);
  }
  public updateFormFieldValue() {
    console.log(
      'ZoneDebug',
      'FormFieldDialogComponent.updateFormFieldValue',
      'should be true:',
      NgZone.isInAngularZone()
    );
    if (this.documentDataService.isAnswerChanged(this.formField, this.value)) {
      this.updateFormFieldByEmit();
    }
  }

  public updateFormFieldDateValue() {
    console.log(`updating date formField ......`);
    console.log(`field: `, this.formField);
    console.log(`value: `, this.value);
    if (this.documentDataService.isAnswerChanged(this.formField, this.value)) {
      this.updateFormFieldByEmit();
    }
  }

  private updateFormFieldByEmit() {
    const formField = this.formField !== '' ? this.formField : this.formFieldToUpdate;
    const enteredValue = this.value;
    this.isDirty = false;
    this.valueChanged.emit({ key: formField, value: enteredValue });
    return true;
  }

  private async updateFormFieldWithAwait() {
    console.log('Update with await');
    const formField = this.formField !== '' ? this.formField : this.formFieldToUpdate;
    const enteredValue = this.value;
    this.isDirty = false;
    await this.parent.onFormFieldValueChanged(formField, enteredValue);
    console.log('END update with await');

    return true;
  }

  public openModal() {
    //if (this.textControlService.isFormFieldFocused) {
    //  if (this.documentDataService.isAnswerChanged(this.formField, this.value)) {
    //    console.debug('Opening modal emitting new field change');
    //    this.updateFormFieldByEmit();
    //  }
    //}
    this.isCalculatedField = false;
    if (this.modal) {
      if (this.docked) {
        this.textControlService.showFieldNavigatorSideBar(true);
        this.setFormFieldPosition();
      } else {
        this.modal.style.zIndex = '2';
      }
      this.changeDetectorRef.detectChanges();

      this.isModalVisible = true;

      TXTextControl.formFields.getItem((field: any) => {
        this.ngZone.run(() => {
          if (field) {
            //make sure token description is evaluated and is === to "" every pass - it will be set below if needed
            this.tokenDescription = '';
            field.getName((name: string) => {
              this.formField = name;
              if (this.formField.startsWith('I')) {
                const obj = this.documentDataService.getObject(this.formField);
                if (obj != null && obj?.qToken) {
                  this.formField = obj.qToken;
                } else {
                  this.formField = '';
                  this.tokenDescription = obj?.description;
                  this.isCalculatedField = true;
                  // return;
                }
              }

              this.obj = this.documentDataService.getObject(this.formField);
              if (this.obj?.qType !== 'T' && this.obj?.qType !== 'E' && this.obj?.qType !== 'C') {
                // is not text field
                this.formField = '';
                this.isCalculatedField = true;
                // return;
              }
              this.ngZone.run(() => {
                this.setFormFieldHelp();
                this.changeDetectorRef.detectChanges();

                field.getText((t: string) => {
                  this.value = t;
                  this.inputPosition = this.value.length;
                  this.selectionEnd = this.value.length;
                  // get qToken value instead when we have an I-code field with all caps value
                  const answer = this.documentDataService.getStoredAnswer(this.formField);
                  if (answer) {
                    this.value = smartenQuotes(answer.answer);
                  }
                  this.formFieldToUpdate = this.formField;

                  if (this.value == null || this.value === '') {
                    this.getDefault();
                  }
                  field.getBounds((bounds: any) => {
                    this.ngZone.run(() => {
                      this.fldPos = bounds;
                      this.setFormFieldPosition();
                      if (this.textControlService.isFormFieldFocused) {
                        this.focusInput();
                      }
                      // update input position in case the user uses the mouse to move the cursor in the input
                      const input = document.getElementById('formFieldValue');
                      input?.addEventListener('click', this.updateInputPosition.bind(this));
                      input?.addEventListener('keyup', this.updateInputPosition.bind(this));
                      this.changeDetectorRef.detectChanges();
                    });
                  });
                });
              });
            });
          }
        });
      });
    }
  }
  public focusInput() {
    const input = document.getElementById('formFieldValue') as HTMLInputElement;
    if (input) {
      input.focus();
      setTimeout(() => {
        if (document.activeElement !== input) {
          input.focus();
          input.selectionStart = this.inputPosition;
          input.selectionEnd = this.inputPosition;
        }
      }, 1000);
    }
  }

  public toggleDock() {
    this.docked = !this.docked;
    this.dockedChanged.emit(this.docked);

    this.focusInput();

    this.changeDetectorRef.detectChanges();
    this.ngZone.runOutsideAngular(() => {
      this.setFormFieldPositionWithCallBack(() => {
        // reenter the Angular zone and display done
        this.ngZone.run(() => {});
      });
    });
  }

  setFormFieldPositionWithCallBack(doneCallback: () => void) {
    this.setFormFieldPosition();
    doneCallback();
  }

  public setDefault() {
    console.log('ZoneDebug', 'FormFieldDialogComponent.setDefault', 'should be true:', NgZone.isInAngularZone());
    if (this.obj) {
      let token;
      if (this.obj.objType === 'I' && this.obj.qType) {
        token = this.obj.qToken;
      }
      if (this.obj.objType === 'Q') {
        token = this.obj.token;
      }
      if (!token) {
        return;
      }
      const def: UserDefModel = {
        praSysId: this.documentDataService.getPraSysId(),
        token: this.obj.token,
        answer: this.value,
      };
      this.userDefService
        .setDefault(def)
        .pipe(take(1))
        .subscribe({
          next: (res: any) => {
            this.toastService.show(
              `The answer has successfully been set as default for ${this.obj?.description ?? 'this question'}.`,
              {
                classname: 'bg-success',
                autohide: true,
              }
            );
          },
          error: (error: any) => {
            this.toastService.show('Failed to set as default.', {
              classname: 'bg-danger',
              autohide: false,
            });
          },
        });
    }
  }

  private getShortcuts() {
    this.shortcutService.getUserShortcuts().subscribe((res: ShortcutModel[]) => {
      if (res) {
        // filter out shortcuts that don't have a key
        this.shortcuts = res
          .filter((s) => s.shortcut && s.shortcut !== '' && s.keycode && s.keycode !== '')
          .map((sm: ShortcutModel) => {
            const input: ShortcutInput = {
              key: this.manipulateShortCutKey(sm.shortcut), //format shortcut for ng package
              command: (output: ShortcutEventOutput) => this.insertQuicktext(sm.text),
              description: sm.title,
              preventDefault: true,
              allowIn: [AllowIn.Input, AllowIn.Textarea],
            };
            return input;
          });
      } else {
      }
    });
  }

  private manipulateShortCutKey(shortcut: string) {
    let lowerCaseShortcut = shortcut.toLocaleLowerCase();
    if (lowerCaseShortcut.includes('ctrl')) {
      shortcut = lowerCaseShortcut.replace('ctrl', 'ctrl +');
    } else if (lowerCaseShortcut.includes('alt')) {
      shortcut = lowerCaseShortcut.replace('alt', 'alt +');
    } else if (lowerCaseShortcut.includes('shift')) {
      shortcut = lowerCaseShortcut.replace('shift', 'shift +');
    }
    return shortcut;
  }

  private insertQuicktext(text: string) {
    this.insertText(text, false);
  }

  private getDefault(): void {
    if (this.obj) {
      const def: UserDefModel = {
        praSysId: this.documentDataService.getPraSysId(),
        user: '',
        token: this.obj.token,
        answer: '',
        answerChoice: '',
      };

      this.userDefService.getDefault(def).subscribe((res) => {
        if (res) {
          this.value = res.answer;
        }
      });
    }
  }

  private setFormFieldHelp() {
    if (this.obj) {
      this.helpText = this.obj.help;
      this.helpDescription = this.obj.description;

      const textOnly = this.helpText.replace(stripHtmlRegex, '').replace(/&nbsp;/gi, ' ');
      this.showHelpText = textOnly?.length > 0;
    } else {
      this.helpText = '';
      this.helpDescription = '';
      this.showHelpText = false;
    }
  }

  private isFirstRectInSecondRect(childRect: DOMRect, parentRect: DOMRect): boolean {
    return (
      childRect.top >= parentRect.top &&
      childRect.left >= parentRect.left &&
      childRect.bottom <= parentRect.bottom &&
      childRect.right <= parentRect.right
    );
  }

  private setFormFieldPosition(skipboundCheck = false) {
    if (this.modal && this.fieldNavigatorSideBar) {
      const detectorElement = this.scrollDetector.nativeElement.parentElement;

      let questionRect = detectorElement.getBoundingClientRect();

      const fieldNavigatorSideBarRect = this.fieldNavigatorSideBar.getBoundingClientRect();

      if (this.mainCanvas) {
        if (this.docked && fieldNavigatorSideBarRect.width > 0) {
          this.modal.style.zIndex = '50';
          this.modal.style.top = `${fieldNavigatorSideBarRect.top + 50}px`;
          this.modal.style.left = `${fieldNavigatorSideBarRect.left + 8}px`;
          this.modal.style.width = `${fieldNavigatorSideBarRect.width - 15}px`;
        }

        if (!this.docked && this.isModalVisible) {
          this.modal.style.zIndex = '2';
          this.modal.style.width = `${this.width}px`;

          if (this.txtViewLoc != null && this.fldPos != null) {
            // form field position x and y values are in twips. 1 pixel = 15 twips
            const rect = this.mainCanvas.getBoundingClientRect();
            let x = 0;
            let y = 0;
            if (this.attachBottomOfField) {
              x = rect.left + (this.fldPos.location.x / 15 - this.txtViewLoc.x) * this.zoom;
              y = rect.top + ((this.fldPos.location.y + this.fldPos.size.height) / 15 - this.txtViewLoc.y) * this.zoom;
            } else {
              // find the bottom of the rect and place that at the top of the modal
              x = rect.left + (this.fldPos.location.x / 15 - this.txtViewLoc.x) * this.zoom;
              y = rect.top + (this.fldPos.location.y / 15 - this.txtViewLoc.y) * this.zoom - questionRect.height;
            }
            console.log('setting modal to position:', x, y);
            console.log('AttachToBottomOfField:', this.attachBottomOfField);
            this.modal.style.top = `${y}px`;
            this.modal.style.left = `${x}px`;

            // Check if any part of the element is within the window bounds
            const mainCanvasRect = document.getElementById('mainCanvas')!.getBoundingClientRect();

            questionRect = detectorElement.getBoundingClientRect();

            this.isInBounds = this.isFirstRectInSecondRect(questionRect, mainCanvasRect);

            console.log('is in bounds', this.isInBounds);
            console.log('skip bound check', skipboundCheck);

            if (!this.isInBounds && !skipboundCheck) {
              if (mainCanvasRect.top > questionRect.top) {
                this.attachBottomOfField = true;
              } else if (mainCanvasRect.bottom < questionRect.bottom) {
                this.attachBottomOfField = false;
              }
              this.setFormFieldPosition(true);
            }

            this.setTimeoutForScrollChanges();
          }
        }
      }
    }
  }

  private setTimeoutForScrollChanges() {
    if (this.timeoutIdForScrollChanges) {
      this.clearTimeoutForScrollChanges();
    }

    this.timeoutIdForScrollChanges = setTimeout(() => {
      if (
        this.helpTextContainer &&
        this.helpTextContainer.nativeElement.scrollHeight <= this.helpTextContainer.nativeElement.clientHeight
      ) {
        //this.showMoreButton = false;
      } else {
        this.showMoreButton = true;
      }
      //this.changeDetectorRef.detectChanges();
    }, 0);
  }

  private clearTimeoutForScrollChanges() {
    clearTimeout(this.timeoutIdForScrollChanges);
  }

  public noSearch: (text$: Observable<string>) => Observable<readonly string[]> = (text$: Observable<string>) =>
    text$.pipe(map(() => []));

  public search: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => (this.searching = true)),
      switchMap((term) =>
        this.autoCompleteService
          .getAutoComplete(term, this.obj?.autoComplete ?? '', this.documentDataService.getCasId())
          .pipe(
            tap(() => (this.searchFailed = false)),
            catchError(() => {
              this.searchFailed = true;
              return of([]);
            })
          )
      ),
      tap(() => (this.searching = false))
    );

  onFocusFormField() {
    this.textControlService.isFormFieldFocused = true;
    this.changeDetectorRef.detectChanges();
  }

  onBlurFormField($event: any) {
    console.log('CM =>> FORM FIELD BLUR', $event);
    if ($event.relatedTarget) {
      this.textControlService.isFormFieldFocused = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  onKeydown(event: KeyboardEvent) {
    const key = event.shiftKey ? `Shift${event.key}` : event.key;
    switch (key) {
      case "'":
        event.preventDefault();
        event.stopPropagation();
        this.insertTextAtCursor('’');
        break;
      case 'Shift"':
        event.preventDefault();
        event.stopPropagation();
        if (this.isCursorPrecededByWhitespaceOrStart()) {
          this.insertTextAtCursor('“');
        } else {
          this.insertTextAtCursor('”');
        }
        break;
      case 'Enter':
        if (this.textControlService.isFormFieldFocused && this.obj?.columns == 0 && this.obj.rows == 0) {
          this.updateAndNavigate(1, event);
        }
        break;
      case 'Tab':
        if (this.obj?.columns == 0 && this.obj.rows == 0) {
          console.log('START Tab and PageDown Clicked');
          this.updateAndNavigate(1, event);
          console.log('END Tab and PageDown Clicked');
        } else {
          this.insertText('\t', true);
          event.preventDefault();
          event.stopPropagation();
        }
        break;
      case 'PageDown':
        console.log('START Tab and PageDown Clicked');
        this.updateAndNavigate(1, event);
        console.log('END Tab and PageDown Clicked');
        break;
      case 'PageUp':
        console.log('PageUp Clicked');
        this.updateAndNavigate(-1, event);
        break;
      case 'ShiftTab':
        if (this.obj?.columns == 0 && this.obj.rows == 0) {
          console.log('Shift Tab Clicked');
          this.updateAndNavigate(-1, event);
        } else {
          event.preventDefault();
          event.stopPropagation();
        }
        break;
      default:
        this.isDirty = true;
        break;
    }
  }

  public updateAndNavigate(direction: number, event?: KeyboardEvent) {
    event?.preventDefault();
    event?.stopPropagation();
    console.log('-->UpdateAndNavigate', direction);
    if (this.isDirty) {
      const formField = this.formField !== '' ? this.formField : this.formFieldToUpdate;
      this.updateFormFieldWithAwait();
      this.navigate(direction);
    } else {
      this.navigate(direction);
    }
  }

  navigate(direction: number) {
    this.navigateToNext.emit(direction);
  }

  onElementScroll(e: Event) {
    this.isScrolled = (e.target as HTMLElement).scrollTop > 0;
    this.changeDetectorRef.detectChanges();
  }

  openQuickTextDialog() {
    const input = document.getElementById('formFieldValue') as HTMLInputElement;

    if (input) {
      this.inputPosition = input.selectionStart ?? 0;
      this.selectionEnd = input.selectionEnd ?? 0;
    }

    const quicktextDialogRef = this.dialogService.openCustomDialog('Quicktext', QuicktextDialogComponent, {
      size: 'lg',
      backdrop: 'static',
    });

    quicktextDialogRef.result.then((text: string) => {
      this.insertText(text, false);
      this.updateFormFieldValue();
    });

    this.changeDetectorRef.detectChanges();
  }

  private insertText(text: string, insertedByKeyPress: boolean = false) {
    if (text) {
      const input = document.getElementById('formFieldValue') as HTMLInputElement;

      if (!input) {
        return;
      }

      const newValue = input.value.slice(0, this.inputPosition) + text + input.value.slice(this.selectionEnd);
      this.value = newValue;
      const inputPosition = this.inputPosition + text.length;

      // if insertedByKeyPress == true, we are already in the input and no need to focus, we just need to set the cursor position
      // doing it with a timeout as a "hack", otherwise couldn't get it to appear at the correct position, it was always in the end
      if (insertedByKeyPress) {
        setTimeout(() => {
          input.selectionStart = inputPosition;
          input.selectionEnd = inputPosition;
        }, 0);
      } else {
        this.focusInput();
      }
    }
  }

  public updateInputPosition() {
    const input = document.getElementById('formFieldValue') as HTMLInputElement;
    if (input) {
      this.inputPosition = input.selectionStart ?? 0;
      this.selectionEnd = input.selectionEnd ?? 0;
    }
  }
  public insertTextAtCursor(text: string) {
    const textElement = document.getElementById('formFieldValue') as HTMLInputElement;
    // Check if the browser supports the selectionStart property
    if (textElement.selectionStart || textElement.selectionStart === 0) {
      const startPos = textElement.selectionStart;
      const endPos = textElement.selectionEnd;
      const originalText = textElement.value;

      // Insert the text at the cursor position
      textElement.value =
        originalText.substring(0, startPos) + text + originalText.substring(endPos ?? startPos, originalText.length);
      // force update the model
      this.value = textElement.value;
      // Move the cursor to the end of the inserted text
      const cursorPosition = startPos + text.length;
      textElement.selectionStart = textElement.selectionEnd = cursorPosition;
    } else {
      // Fallback: append the text at the end
      textElement.value += text;
    }
    this.isDirty = true;
    textElement.focus();
  }
  
  public isCursorPrecededByWhitespaceOrStart(): boolean {
    const textElement = document.getElementById('formFieldValue') as HTMLInputElement;
    const cursorPos = textElement.selectionStart;

    // If cursor is at the start, return true
    if (cursorPos === 0 || cursorPos === null) {
      return true;
    }

    // Get the character before the cursor
    const precedingChar = textElement.value.charAt(cursorPos - 1);

    // Check for space, tab, or newline
    return precedingChar === ' ' || precedingChar === '\t' || precedingChar === '\n' || precedingChar === '\r'
    || precedingChar === '(' || precedingChar === '[' || precedingChar === '{';
  }
}
